import { z } from 'zod';
/**
 * Le forme delle fasi del contratto n. 1 «L'assetto organizzativo ideale» costruite sopra i passi della configurazione
 * (docs/contratti, ADR 0153, 0156, 0157).
 */

/** Da dove viene un obbligo del registro (fase 4): l'anagrafica dell'impresa, il registro di un impianto, un passo. */
export type ObligationOrigin = 'impresa' | 'impianti' | 'sedi' | 'attivita' | 'software' | 'organizzazione' | 'procedure';

/** La fase che realizza un obbligo: un processo o una procedura, un impianto, una piattaforma, un incarico di un ruolo. */
export interface ObligationRealizationDto {
  fase: 'impianti' | 'programmi' | 'organizzazione' | 'procedure';
  voce: string;
}

/** Un obbligo del registro unico (contratto, fase 4). */
export interface ObligationEntryDto {
  chiave: string;
  /** L'ambito, in parole semplici (società e fisco, lavoro, sicurezza, ambiente, impianti, settore…). */
  area: string | null;
  /** La norma, come la scrive la fonte. */
  norma: string;
  /** Cosa chiede, in una frase. */
  cosa: string;
  /** Il fatto dell'azienda che lo fa scattare. */
  perche: string | null;
  /** Ogni quanto o entro quando. */
  cadenza: string | null;
  /** Il ruolo che ne risponde, quando una fase l'ha già detto; null = da assegnare (art. 7.16). */
  chi: string | null;
  /** Cosa resta a dimostrarlo. */
  prova: string | null;
  /** Le sedi o le cose a cui si applica; vuoto = tutta l'azienda. */
  riguarda: string[];
  /** Da dove viene, una o più fonti dopo l'unione dei doppioni. */
  da: ObligationOrigin[];
  /** Le voci delle fasi 6-9 che lo realizzano; vuoto = nessuna fase lo realizza ancora. */
  realizzato_da: ObligationRealizationDto[];
  certezza: 'confermato' | 'da_confermare';
}

export interface ObligationRegistryDto {
  obblighi: ObligationEntryDto[];
  /** Quanti obblighi nessuna fase realizza ancora: vanno alla diagnosi (fase 10). */
  scoperti: number;
  /** Quanti hanno il ruolo che ne risponde. */
  con_responsabile: number;
  /** Per area, quanti obblighi. */
  per_area: Array<{ area: string; obblighi: number }>;
}

/** Il ruolo che il cliente dichiara entrando (contratto, fase 1 e art. 3-ter). */
export interface ClientRoleDto {
  /** Il nome del ruolo, dall'organigramma ideale o uno dei ruoli esterni (titolare, commercialista…). */
  ruolo: string;
  /** Il ruolo dichiarato da chi non è il titolare vale dopo la sua conferma. */
  confermato: boolean;
}

/** I ruoli che chi entra può dichiarare, oltre a quelli dell'organigramma. */
export const CLIENT_ROLES_OUTSIDE = ['Titolare', 'Socio', 'Commercialista o consulente esterno'] as const;

export const ClientRoleSchema = z.object({ ruolo: z.string().trim().min(2).max(120) }).strict();
export type ClientRoleInput = z.infer<typeof ClientRoleSchema>;

/** Un tipo di scarto della diagnosi (fase 10), misurabile senza il modello. */
export const GAP_KINDS = ['obbligo_scoperto', 'lavoro_a_mano', 'persone', 'impianto_da_verificare'] as const;
export type GapKind = (typeof GAP_KINDS)[number];

export interface GapDto {
  tipo: GapKind;
  testo: string;
  /** La fase e la voce da cui nasce (contratto, fase 10: ogni scarto rimanda alla sua voce). */
  fonte: { fase: string; voce: string };
  /** La proposta che lo chiude (fase 11), se c'è. */
  proposta_id: string | null;
}

/** Ore ed euro di un reparto o di un ruolo, calcolati dal server con la formula visibile (art. 7.13). */
export interface BenefitDto {
  ore_mese: number;
  euro_anno: number | null;
  /** Persone del reparto o del ruolo, se il progetto le dice: le ore per dipendente. */
  persone: number | null;
  ore_per_dipendente: number | null;
  /** La formula in parole, con le ipotesi. */
  formula: string;
  stima: 'da_confermare' | 'stimata' | 'misurata';
}

export interface DepartmentDiagnosisDto {
  sede_id: string;
  sede: string;
  reparto_id: string;
  reparto: string;
  scarti: GapDto[];
  benefici: BenefitDto | null;
}

export interface DiagnosisDto {
  reparti: DepartmentDiagnosisDto[];
  /** Gli scarti di tutta l'azienda (obblighi scoperti, persone per sede). */
  azienda: GapDto[];
  totale: BenefitDto | null;
}

/** Una riga della dashboard d'esempio di un ruolo (fase 12). */
export interface RoleDashboardLineDto {
  testo: string;
  /** Quando: oggi, questa settimana, entro una scadenza. */
  quando: string | null;
  fonte: string;
}

export interface RoleDashboardDto {
  ruolo: string;
  sede: string | null;
  reparto: string | null;
  /** Sempre true: dati d'esempio realistici, mai dati veri di persone. */
  esempio: true;
  oggi: RoleDashboardLineDto[];
  scadenze: RoleDashboardLineDto[];
  procedure: RoleDashboardLineDto[];
  programmi: string[];
  /** Cosa fa Timonio (o l'agente) per questo ruolo, dalle proposte del suo reparto. */
  agente: RoleDashboardLineDto[];
  benefici: BenefitDto | null;
  /** L'inefficienza che la dashboard toglie, in una riga, con lo scarto della diagnosi. */
  inefficienza: string | null;
  /** Le funzioni mostrate che Timonio non ha ancora (art. 8, fase 12: «in arrivo»). */
  in_arrivo: string[];
}

/** Un obiettivo del piano (fase 13). */
export interface PlanGoalDto {
  id: string;
  titolo: string;
  reparto: string;
  sede: string;
  /** Il punto di partenza, con la fonte. */
  partenza: string;
  traguardo: string;
  scadenza_giorni: 30 | 60 | 90;
  fa_timonio: string;
  fa_azienda: string;
  misura: string;
  benefici: BenefitDto;
  /** La proposta di efficientamento da cui nasce: accettarla accetta l'obiettivo. */
  proposta_id: string;
  stato: 'proposto' | 'accettato' | 'rifiutato' | 'rimandato';
  /** Il ruolo del cliente che lo vede per primo; null = tutta l'azienda. */
  perimetro: string | null;
}

/** La misura nel tempo di un obiettivo accettato (fase 14): promesso accanto a misurato, mai stime. */
export interface GoalMeasureDto {
  obiettivo_id: string;
  titolo: string;
  accettato_il: string | null;
  promesso_ore_mese: number;
  /** null finché Timonio non ha dati veri (compiti e procedure svolte). */
  misurato_ore_mese: number | null;
  nota: string;
}

/** Le fasi 1 e 10-14 del contratto insieme, viste dal ruolo del cliente. */
export interface ContractSummaryDto {
  ruolo_cliente: ClientRoleDto | null;
  /** I ruoli tra cui scegliere: quelli dell'organigramma ideale e quelli esterni. */
  ruoli: string[];
  /** Gli attributi di ogni sede (fase 2). */
  sedi: SiteAttributesDto[];
  /** I processi aziendali (fase 5). */
  processi: ProcessDto[];
  /** I programmi in tre famiglie (fase 7). */
  programmi: ProgramDto[];
  diagnosi: DiagnosisDto;
  dashboard: RoleDashboardDto[];
  piano: PlanGoalDto[];
  misura: GoalMeasureDto[];
  /** Le proposte si stanno ancora scrivendo: la pagina si ricontrolla. */
  in_preparazione: boolean;
  /** Quanto è costata finora la configurazione (art. 11). */
  costo?: ConfigurationCostDto;
}

/** Un processo aziendale (contratto, fase 5): primario se fa il prodotto o il servizio, di supporto se lo sostiene. */
export interface ProcessDto {
  nome: string;
  tipo: 'primario' | 'supporto';
  /** Le sedi in cui si svolge. */
  sedi: string[];
  /** Le fasi nell'ordine, con i ruoli che le svolgono e i programmi in cui servono. */
  fasi: Array<{ nome: string; ruoli: string[]; programmi: string[]; entra?: string | null; esce?: string | null }>;
  /** Il ruolo che ne risponde; null = da assegnare. */
  responsabile: string | null;
  /** Gli obblighi del registro che realizza (fase 4). */
  obblighi: string[];
  /** Da dove viene: l'attività del passo Attività o l'area delle procedure. */
  fonte: string;
}

/** Le tre famiglie dei programmi (contratto, fase 7). */
export const PROGRAM_FAMILIES = ['piattaforma_pubblica', 'processi', 'quotidiano'] as const;
export type ProgramFamily = (typeof PROGRAM_FAMILIES)[number];

export interface ProgramDto {
  nome: string;
  famiglia: ProgramFamily;
  /** Per le piattaforme pubbliche: il nome ufficiale del servizio dell'ente. */
  servizio: string | null;
  norma: string | null;
  obbligatoria: boolean;
  /** Il prodotto vero, solo con la fonte che lo mostra. */
  prodotto: string | null;
  /** Per le piattaforme pubbliche: cosa serve per entrare, chi lo fa con delega, la sanzione. */
  accesso: string | null;
  delega: string | null;
  sanzione: string | null;
  /** Il ruolo che ne risponde (dall'organigramma); null = da assegnare. */
  risponde: string | null;
  /** Le fasi o i processi in cui serve. */
  fasi: string[];
  /** Dove: «tutta l'impresa» o il nome della sede. */
  dove: string;
}

/**
 * Il grado di fiducia di una fonte (contratto n. 1 versione 2.0, art. 3.51 e 5.2): A ufficiale, B dell'azienda, C terza
 * parte affidabile, D aperta, T dichiarato dal titolare (le sue risposte e correzioni, con la data).
 */
export type SourceGrade = 'A' | 'B' | 'C' | 'D' | 'T';

/**
 * Il grado dal tipo di fonte, con criteri fissi e senza il cervello: visura, bilancio e documenti ufficiali A; il sito
 * verificato dell'azienda B; le pagine pubbliche in cui il server trova la partita IVA (registro) C; ciò che dice il
 * titolare T (ADR 0171); il resto D. Una pagina online senza la partita IVA non entra da sola (art. 5.2).
 */
export function sourceGrade(fonte: string | null | undefined): SourceGrade {
  switch (fonte) {
    case 'visura':
    case 'documento':
    case 'bilancio':
    case 'xbrl':
      return 'A';
    case 'sito':
      return 'B';
    case 'registro':
      return 'C';
    case 'persona':
      return 'T';
    default:
      return 'D';
  }
}

export const SOURCE_GRADE_TEXT: Record<SourceGrade, string> = {
  A: 'fonte ufficiale',
  B: 'dal sito dell’azienda',
  C: 'fonte affidabile verificata',
  D: 'da confermare',
  T: 'detto dal titolare',
};

/** Un attributo di una sede (contratto, fase 2), con la fonte o lo stato. */
export interface SiteAttributeDto {
  gruppo: string;
  attributo: string;
  valore: string | null;
  fonte: string | null;
  /** Il collegamento alla pagina, per i fatti trovati online. */
  url: string | null;
  grado: SourceGrade | null;
  /** trovato: c'è con la fonte; da_confermare: lo sa il cliente; da_collegare: serve una fonte che Timonio non legge ancora. */
  stato: 'trovato' | 'da_confermare' | 'da_collegare';
}

export interface SiteAttributesDto {
  sede_id: string;
  sede: string;
  attributi: SiteAttributeDto[];
  /** Quota di attributi trovati senza chiedere al cliente (obiettivo del contratto: almeno 70%). */
  trovati: number;
}

/** Quanto è costata la configurazione dell'azienda (contratto, art. 11): spesa del motore, divisa per scopo. */
export interface ConfigurationCostDto {
  totale_eur: number;
  chiamate: number;
  per_scopo: Array<{ scopo: string; eur: number; chiamate: number }>;
  /** L'obiettivo del contratto per una nuova configurazione, in euro. */
  obiettivo_eur: { min: number; max: number };
}

/** Il cliente conferma un attributo della sede che solo lui conosce (contratto, fase 2: decide il cliente). */
export const SiteAttributeSchema = z.object({ sede_id: z.string().uuid(), attributo: z.string().trim().min(2).max(120), valore: z.string().trim().min(1).max(300) }).strict();
export type SiteAttributeInput = z.infer<typeof SiteAttributeSchema>;


/**
 * La pagina «Al timone» (contratto n. 1, fase 13; ADR 0162): ciò che il server compone con le voci delle fasi 0-12 e
 * i testi che il cervello scrive per convincere il cliente ad acquistare l'abbonamento, nello stile «Sinapsi».
 */

/** I livelli aziendali delle dashboard mostrate: dall'organigramma, senza conoscenza di settore. */
export const TIMONE_LEVELS = ['direzione', 'responsabile', 'operativo'] as const;
export type TimoneLevel = (typeof TIMONE_LEVELS)[number];

/** Le sezioni della pagina che il cervello mette in ordine; mappa, pianta e ruoli ci sono sempre. */
export const TIMONE_SECTIONS = ['mappa', 'pianta', 'ruoli', 'calcolo', 'rotta', 'promessa'] as const;
export type TimoneSection = (typeof TIMONE_SECTIONS)[number];
export const TIMONE_REQUIRED: readonly TimoneSection[] = ['mappa', 'pianta', 'ruoli'];

/** Un nodo della mappa di Timonio, con la fonte che si apre toccandolo. */
export interface TimoneNodeDto {
  nome: string;
  /** La riga sotto il nome: il grado, la famiglia, il numero. */
  sotto: string | null;
  grado: SourceGrade | null;
  /** Da dove viene, in parole. */
  fonte: string;
}

export interface TimoneMapDto {
  /** Sopra: le fonti davvero usate. */
  fonti: TimoneNodeDto[];
  /** A sinistra: piattaforme pubbliche e programmi dei processi (fase 7, famiglie A e B). */
  programmi: TimoneNodeDto[];
  /** A destra: gli strumenti di ogni giorno (famiglia C). */
  quotidiani: TimoneNodeDto[];
  /** Sotto: ciò che Timonio restituisce all'azienda. */
  restituisce: TimoneNodeDto[];
  /** Ciò che non compare e perché, in una nota grigia. */
  esclusi: string[];
}

export interface TimonePlantDto {
  sedi: Array<{
    nome: string;
    luogo: string | null;
    addetti: number | null;
    reparti: Array<{ nome: string; persone: number | null; ruoli: string[]; ore_mese: number | null }>;
  }>;
  ore_mese: number | null;
}

/** Una dashboard mostrata, con il livello e cosa vede e fa il dipendente grazie a Timonio. */
export interface TimoneRoleDto {
  livello: TimoneLevel;
  dashboard: RoleDashboardDto;
  vede: string[];
  fa: string[];
}

/** Il prezzo del listino (art. 11.6): 20% del valore mensile della fase 11, 30 giorni di prova. */
export interface TimonePriceDto {
  /** Valore mensile del tempo e dei costi risparmiati (fase 11), in euro; null se il calcolo in euro non è completo. */
  valore_mese: number | null;
  ore_mese: number | null;
  prezzo_mese: number | null;
  quota: number;
  prova_giorni: number;
  /** Il valore medio di un'ora liberata, dal calcolo della fase 11. */
  costo_orario: number | null;
  giorni_per_ripagare: number | null;
  perche_senza_prezzo: string | null;
  formula: string;
}

export interface TimoneCopyDto {
  occhiello: string;
  titolo: string;
  sottotitolo: string;
  ordine: TimoneSection[];
  sezioni: Record<TimoneSection, { titolo: string; testo: string }>;
  chiusura: { titolo: string; testo: string };
  /** cervello = scritti dal motore e controllati; server = testi di riserva composti dal server. */
  fonte: 'cervello' | 'server';
}

export interface TimonePurchaseDto {
  richiesto_il: string;
  ruolo: string | null;
  prezzo_mese: number | null;
  valore_mese: number | null;
  prova_giorni: number;
}

export interface TimoneDto {
  azienda: { nome: string; luogo: string | null; addetti: number | null; in_breve: string | null };
  ruolo_cliente: string | null;
  mappa: TimoneMapDto;
  pianta: TimonePlantDto;
  ruoli: TimoneRoleDto[];
  prezzo: TimonePriceDto;
  rotta: PlanGoalDto[];
  promessa: GoalMeasureDto[];
  testi: TimoneCopyDto;
  acquisto: TimonePurchaseDto | null;
  /** La prova dei ruoli (contratto n. 2, art. 6.8): il link che il titolare ha creato e com'è usato; null se non c'è. */
  prova: ProvaRuoliLinkDto | null;
  /** Le proposte o i testi del cervello si stanno ancora scrivendo: la pagina si ricontrolla. */
  in_preparazione: boolean;
}

// --- La prova dei ruoli (contratto n. 2, art. 6.8; ADR 0173) ---

/** Una proposta d'acquisto fatta da una persona dalla prova dei ruoli (art. 3.17): il ruolo e una riga, mai il nome. */
export interface ProvaPropostaDto {
  ruolo: string;
  quando: string;
  riga: string | null;
}

/** Il link di prova (art. 3.16) visto dal titolare: l'indirizzo, quando l'ha creato, le aperture per ruolo e le proposte. */
export interface ProvaRuoliLinkDto {
  url: string;
  creato_il: string;
  /** Quante volte ogni ruolo è stato aperto dalla prova; mai chi. */
  aperture: Array<{ ruolo: string; volte: number }>;
  proposte: ProvaPropostaDto[];
}

export type ProvaCompetenzaGruppo = 'conoscenza' | 'abilita' | 'mestiere';

/** Una competenza della scheda della professione del ruolo, con cosa cambia con Timonio (art. 6.8.4). */
export interface ProvaCompetenzaDto {
  testo: string;
  fonte: string;
  gruppo: ProvaCompetenzaGruppo;
  /** Cosa cambia con Timonio per questa competenza; null = resta sua, Timonio non c'entra. */
  cambia: string | null;
  /** La voce del progetto da cui nasce il «cosa cambia»: una procedura, un lavoro dell'agente, un programma, una scadenza. */
  voce: string | null;
}

/** La prova di un ruolo: la sua dashboard d'esempio senza euro, le competenze e la crescita (art. 6.8.4-6.8.5). */
export interface ProvaRuoloDto {
  ruolo: string;
  sede: string | null;
  reparto: string | null;
  livello: TimoneLevel;
  /** Il nome ufficiale della professione (fase 8), se la scheda c'è. */
  professione: string | null;
  dashboard: RoleDashboardDto;
  vede: string[];
  fa: string[];
  competenze: ProvaCompetenzaDto[];
  crescita: { da_aggiornare: Array<{ testo: string; fonte: string }>; ruoli_vicini: string[] };
  testi: { titolo: string; sottotitolo: string; fonte: 'cervello' | 'server' };
  /** I testi del cervello si stanno scrivendo: la pagina si ricontrolla. */
  in_preparazione: boolean;
}

/** La pagina pubblica della prova dei ruoli: l'azienda, i ruoli tra cui scegliere e, se scelto, il ruolo. */
export interface ProvaRuoliDto {
  azienda: { nome: string; luogo: string | null };
  ruoli: Array<{ ruolo: string; sede: string | null; reparto: string | null; livello: TimoneLevel }>;
  scelto: ProvaRuoloDto | null;
}

export const ProvaPropostaSchema = z
  .object({ ruolo: z.string().trim().min(1).max(160), riga: z.string().trim().max(300).optional() })
  .strict();
